/**
 * Template library goals / primary categories (merchant rail + DB category.primary).
 * Single source of truth for category keys, labels, and video upsell templates.
 *
 * NOTE: Do not import popup-layouts or template-capabilities here — registry.js
 * imports this module and would create a circular init (TDZ) in the client bundle.
 */

export const TEMPLATE_LIBRARY_CATEGORIES = Object.freeze([
  {
    key: "upsell-crosssell",
    primary: "Upsell & Cross sell",
    label: "Upsell & Cross sell",
    color: "#008060",
  },
  {
    key: "upsell-by-video",
    primary: "Upsell by Video",
    label: "Upsell by Video",
    color: "#7c3aed",
  },
  {
    key: "checkout-extensions",
    primary: "Checkout Extensions",
    label: "Checkout Extensions",
    color: "#2c6ecb",
  },
  {
    key: "email-sms-capture",
    primary: "Email & SMS capture",
    label: "Email & SMS capture",
    color: "#616161",
  },
]);

export const TEMPLATE_GOAL_FILTERS = Object.freeze([
  {
    key: "upsell-recommendation",
    categoryKey: "upsell-crosssell",
    title: "Upsell & Recommendation",
    description: "Boost sales with smart product offers",
    iconName: "money",
    accent: "#b98900",
    tint: "#fff5e5",
  },
  {
    key: "upsell-extensions",
    categoryKey: "checkout-extensions",
    title: "Upsell Extensions",
    description: "Add offers to thank you, checkout, and post-purchase pages",
    iconName: "cart",
    accent: "#2c6ecb",
    tint: "#eaf4ff",
  },
  {
    key: "grow-email-list",
    categoryKey: "email-sms-capture",
    title: "Grow My Email List",
    description: "Build a high-quality email list",
    iconName: "email",
    accent: "#008060",
    tint: "#e6f4ef",
  },
  {
    key: "upsell-by-video",
    categoryKey: "upsell-by-video",
    title: "Upsell by Video",
    description: "Convert shoppers with product and preview video popups",
    iconName: "play",
    accent: "#7c3aed",
    tint: "#f5f3ff",
  },
]);

/** Merchant-facing goals/categories hidden from catalog, dashboard, and analytics (not deleted). */
export const HIDDEN_MERCHANT_CATEGORY_KEYS = Object.freeze([
  "upsell-by-video",
  "email-sms-capture",
]);

export const HIDDEN_TEMPLATE_GOAL_FILTER_KEYS = Object.freeze([
  "grow-email-list",
  "upsell-by-video",
]);

/** OptiMonk-style template browser keys mapped to hidden merchant categories. */
export const HIDDEN_OPTIMONK_TEMPLATE_CATEGORY_KEYS = Object.freeze([
  "upsellByVideo",
  "emailSmsCapture",
]);

export function isHiddenMerchantCategoryKey(key) {
  return HIDDEN_MERCHANT_CATEGORY_KEYS.includes(String(key || ""));
}

export function getVisibleTemplateGoalFilters() {
  return TEMPLATE_GOAL_FILTERS.filter(
    (goal) => !HIDDEN_TEMPLATE_GOAL_FILTER_KEYS.includes(goal.key)
  );
}

export function getVisibleTemplateLibraryCategories() {
  return TEMPLATE_LIBRARY_CATEGORIES.filter(
    (category) => !isHiddenMerchantCategoryKey(category.key)
  );
}

export function isHiddenMerchantTemplate(template) {
  if (!template) return false;
  const key = resolveTemplateLibraryCategoryKey(template);
  return isHiddenMerchantCategoryKey(key);
}

export function popupToMerchantTemplateShape(popup) {
  if (!popup || typeof popup !== "object") return null;
  const embeddedTemplate =
    popup.template_id && typeof popup.template_id === "object"
      ? popup.template_id
      : null;
  return {
    title:
      popup.generalSettings?.title ||
      popup.title ||
      embeddedTemplate?.title,
    category: popup.category || embeddedTemplate?.category,
    template_type:
      popup.popUpType || popup.template_type || embeddedTemplate?.template_type,
    search_tags: popup.search_tags || embeddedTemplate?.search_tags,
  };
}

const PROTECTED_MERCHANT_UPSELL_POPUP_TYPES = new Set(
  [
    "Custom Cart",
    "Product Page Upsell",
    "AI Generated Popup",
    "Volume Discount",
  ].map((type) => type.toLowerCase())
);

/** Keep in sync with POPUP_LAYOUT_IDS in popup-layouts/registry.js */
const PROTECTED_MERCHANT_LAYOUT_IDS = new Set([
  "cart-drawer-v1",
  "product-page-upsell-v1",
  "ai-recommendation-upsell-v1",
  "product-recommendation-v1",
  "social-proof-upsell-v1",
  "volume-discount-v1",
]);

const POPUP_LAYOUT_LEGACY_ALIASES = Object.freeze({
  "ai-conversion-v1": "ai-recommendation-upsell-v1",
  "structured-upsell-v1": "ai-recommendation-upsell-v1",
});

function resolvePopupLayoutIdForVisibility(popup) {
  const gs =
    popup?.generalSettings != null ? popup.generalSettings : popup || {};
  const raw =
    gs.popupLayoutId ||
    gs.layoutPreset ||
    gs.layoutMode ||
    gs.layoutTree?.preset ||
    gs.layoutConfig?.preset ||
    null;
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  return POPUP_LAYOUT_LEGACY_ALIASES[trimmed] || trimmed;
}

/** Upsell campaigns that must stay visible even when legacy category.primary is "General". */
export function isProtectedMerchantUpsellPopup(popup) {
  if (!popup || typeof popup !== "object") return false;

  const popUpType = String(
    popup.popUpType ||
      popup.template_type ||
      popup.template_id?.template_type ||
      ""
  )
    .trim()
    .toLowerCase();

  if (PROTECTED_MERCHANT_UPSELL_POPUP_TYPES.has(popUpType)) return true;
  if (popup.generalSettings?.isStaticCartDrawer === true) return true;
  if (popup.generalSettings?.isStaticProductPageUpsell === true) return true;
  if (popup.generalSettings?.isStaticVolumeDiscount === true) return true;

  const layoutId = resolvePopupLayoutIdForVisibility(popup);
  if (layoutId && PROTECTED_MERCHANT_LAYOUT_IDS.has(layoutId)) return true;

  const primary = String(
    popup.category?.primary || popup.template_id?.category?.primary || ""
  ).toLowerCase();
  if (/checkout extension|upsell extension|upsell & cross/i.test(primary)) {
    return true;
  }

  return false;
}

export function isHiddenMerchantPopup(popup) {
  if (isProtectedMerchantUpsellPopup(popup)) return false;
  const shape = popupToMerchantTemplateShape(popup);
  return shape ? isHiddenMerchantTemplate(shape) : false;
}

export function isHiddenMerchantCampaignRow(row, campaign) {
  if (row?.category?.filter === "upsell") return false;
  if (row?.category?.filter === "lead") return true;
  const popups = campaign?.popups || row?.popups || [];
  return popups.some((entry) => isHiddenMerchantPopup(entry?.popup));
}

export function filterVisibleMerchantCampaignRows(rows = [], campaigns = []) {
  const campaignById = new Map(
    (Array.isArray(campaigns) ? campaigns : []).map((campaign) => [
      String(campaign?._id),
      campaign,
    ])
  );
  return rows.filter((row) => {
    const campaign = campaignById.get(String(row?.id ?? row?.campaignId)) || null;
    return !isHiddenMerchantCampaignRow(row, campaign);
  });
}

export function isHiddenMerchantAnalyticsPopupType(popupType) {
  const normalized = String(popupType || "").toLowerCase();
  if (!normalized) return false;
  if (/video/.test(normalized)) return true;
  return /(email|lead|subscribe|newsletter|sms|capture|collect|visitor|feedback)/.test(
    normalized
  );
}

export function isHiddenOptiMonkTemplateCategoryKey(categoryKey) {
  return HIDDEN_OPTIMONK_TEMPLATE_CATEGORY_KEYS.includes(String(categoryKey || ""));
}

/** Templates that belong in Upsell by Video (exact title match, case-insensitive). */
export const UPSELL_BY_VIDEO_TEMPLATE_TITLES = Object.freeze([
  "Video Preview Popup",
  "Video Slider Block",
  "Product Video Slider",
]);

export const PRIMARY_CATEGORY_SYNONYMS = Object.freeze({
  "upsell & cross sell": "upsell-crosssell",
  "upsell & cross-sell": "upsell-crosssell",
  "upsell and cross sell": "upsell-crosssell",
  "upsell by video": "upsell-by-video",
  "video upsell": "upsell-by-video",
  "checkout extensions": "checkout-extensions",
  "collect visitors data": "email-sms-capture",
  "collect feedback": "email-sms-capture",
  general: "email-sms-capture",
  "email & sms capture": "email-sms-capture",
  "email and sms capture": "email-sms-capture",
});

const CATEGORY_BY_KEY = Object.fromEntries(
  TEMPLATE_LIBRARY_CATEGORIES.map((c) => [c.key, c])
);

const CATEGORY_BY_PRIMARY = Object.fromEntries(
  TEMPLATE_LIBRARY_CATEGORIES.map((c) => [c.primary.toLowerCase(), c.key])
);

export function normalizePrimaryCategoryKey(primary) {
  if (!primary || typeof primary !== "string") return null;
  const normalized = primary
    .trim()
    .toLowerCase()
    .replace(/[.,]/g, "")
    .replace(/\s+/g, " ");
  return PRIMARY_CATEGORY_SYNONYMS[normalized] || CATEGORY_BY_PRIMARY[normalized] || null;
}

export function isUpsellByVideoTemplate(template) {
  const title = String(template?.title || "")
    .trim()
    .toLowerCase();
  return UPSELL_BY_VIDEO_TEMPLATE_TITLES.some(
    (entry) => entry.toLowerCase() === title
  );
}

export function getUpsellByVideoTemplateSortRank(title) {
  const normalized = String(title || "")
    .trim()
    .toLowerCase();
  const idx = UPSELL_BY_VIDEO_TEMPLATE_TITLES.findIndex(
    (entry) => entry.toLowerCase() === normalized
  );
  return idx >= 0 ? idx : 999;
}

/**
 * Resolve a template to a library category key (merchant rail filtering).
 */
export function resolveTemplateLibraryCategoryKey(template) {
  // Curated video templates always map here, even when legacy category.primary is wrong.
  if (isUpsellByVideoTemplate(template)) {
    return "upsell-by-video";
  }

  let key = normalizePrimaryCategoryKey(template?.category?.primary);

  if (!key && template?.template_type) {
    const templateType = template.template_type.toLowerCase();
    if (templateType.includes("upsell") && templateType.includes("video")) {
      key = "upsell-by-video";
    } else if (templateType.includes("upsell") || templateType.includes("cross")) {
      key = "upsell-crosssell";
    } else if (templateType.includes("checkout")) {
      key = "checkout-extensions";
    } else if (templateType.includes("collect") && templateType.includes("visitor")) {
      key = "email-sms-capture";
    } else if (templateType.includes("collect") && templateType.includes("feedback")) {
      key = "email-sms-capture";
    } else if (/(email|sms|newsletter|subscribe|lead|phone|text)/.test(templateType)) {
      key = "email-sms-capture";
    }
  }

  if (!key && Array.isArray(template?.search_tags)) {
    const tags = template.search_tags.map((tag) => String(tag).toLowerCase());
    if (isUpsellByVideoTemplate(template) || tags.some((tag) => tag.includes("video upsell"))) {
      key = "upsell-by-video";
    } else if (tags.some((tag) => tag.includes("upsell") || tag.includes("cross"))) {
      key = "upsell-crosssell";
    } else if (tags.some((tag) => tag.includes("checkout"))) {
      key = "checkout-extensions";
    } else if (tags.some((tag) => tag.includes("collect") && tag.includes("visitor"))) {
      key = "email-sms-capture";
    } else if (tags.some((tag) => tag.includes("collect") && tag.includes("feedback"))) {
      key = "email-sms-capture";
    } else if (tags.some((tag) => /email|sms|subscribe|newsletter|lead|phone/.test(tag))) {
      key = "email-sms-capture";
    }
  }

  key = key || "email-sms-capture";
  if (!CATEGORY_BY_KEY[key]) {
    key = "email-sms-capture";
  }
  return key;
}

export function getTemplateLibraryCategoryMeta(key) {
  return CATEGORY_BY_KEY[key] || CATEGORY_BY_KEY["email-sms-capture"];
}

export function getTemplateLibraryCategoryForTemplate(template) {
  const key = resolveTemplateLibraryCategoryKey(template);
  const meta = getTemplateLibraryCategoryMeta(key);
  return { key, label: meta.label, color: meta.color };
}

/**
 * Template library goals that use a 2-step editor (Elements + Conditions only).
 * Email & SMS capture keeps Integrations for lead-sync providers.
 */
export const TEMPLATE_LIBRARY_TWO_STEP_EDITOR_CATEGORY_KEYS = Object.freeze([
  "upsell-crosssell",
  "checkout-extensions",
  "upsell-by-video",
]);

/**
 * Build a template-shaped object from a campaign popup for category resolution.
 * @param {object | null | undefined} popup
 */
export function resolvePopupEditorCategoryKey(popup) {
  if (!popup || typeof popup !== "object") return null;

  const embeddedTemplate =
    popup.template_id && typeof popup.template_id === "object" ? popup.template_id : null;

  return resolveTemplateLibraryCategoryKey({
    title:
      popup.generalSettings?.title ||
      popup.title ||
      embeddedTemplate?.title,
    category: popup.category || embeddedTemplate?.category,
    template_type:
      popup.popUpType || popup.template_type || embeddedTemplate?.template_type,
    search_tags: popup.search_tags || embeddedTemplate?.search_tags,
  });
}

/**
 * Whether the merchant editor should hide the Integrations wizard step.
 * @param {object | null | undefined} popup
 */
export function usesTwoStepEditorWizard(popup) {
  const key = resolvePopupEditorCategoryKey(popup);
  return (
    Boolean(key) &&
    TEMPLATE_LIBRARY_TWO_STEP_EDITOR_CATEGORY_KEYS.includes(key)
  );
}
